import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import { useDefaultLayoutProps } from '../../layouts/DefaultLayout/useProps'
import { actions, selectors } from '../../redux'
import { Screen } from '../../types/global'
import { useSagaTakeEvery } from '../../hooks/useSaga'

import { ConfirmationTemplateProps } from './index'

export const useConfirmationTemplateProps = (): ConfirmationTemplateProps => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const getEventBySlug = useSelector(selectors.app.getEventBySlug)
  const registerFormValues = useSelector(selectors.app.registerFormValues)
  const register = useSelector(selectors.app.register)
  const selectedSlotId = useSelector(selectors.app.selectedSlotId)

  const layoutProps = useDefaultLayoutProps()
  const $resendTimeout = useRef<NodeJS.Timeout>()

  const [isResendDisabled, setIsResendDisabled] = useState(true)

  const $submittedEmailCodeRef = useRef<string | null>(null)

  const setResendTimeout = useCallback(() => {
    setIsResendDisabled(true)
    $resendTimeout.current = setTimeout(() => {
      setIsResendDisabled(false)
    }, 10000)
  }, [])

  useEffect(() => {
    setResendTimeout()
    return () => {
      clearTimeout($resendTimeout.current)
    }
  }, [setResendTimeout])

  useSagaTakeEvery(
    actions.app.registerSuccess,
    useCallback(() => {
      dispatch(actions.app.reset())
      dispatch(actions.app.setScreen(Screen.success))
    }, [dispatch])
  )

  useSagaTakeEvery(
    actions.app.registerError,
    useCallback(
      (response: any) => {
        const errorEmailCode =
          // eslint-disable-next-line no-unsafe-optional-chaining
          'errorEmailCode' in response?.payload?.[0]?.extensions?.validation

        if (!errorEmailCode) {
          const slug = getEventBySlug.data?.slug
          if (slug) {
            dispatch(actions.app.getEventBySlugRequest({ slug }))
          }
          dispatch(actions.app.setRegisterCode($submittedEmailCodeRef.current!))
          dispatch(actions.app.setScreen(Screen.register))
        }
      },
      [dispatch, getEventBySlug.data?.slug]
    )
  )

  return useMemo(
    () => ({
      layout: layoutProps,
      backLink: {
        text: t('confirmation.back'),
        onClick: () => {
          dispatch(actions.app.setScreen(Screen.register))
        },
      },
      form: {
        formik: {
          initialValues: {
            emailCode: '',
          },
          validationSchema: Yup.object().shape({
            emailCode: Yup.string().required().length(5, t('confirmation.form.validation.otp')),
          }),
          onSubmit: (values) => {
            $submittedEmailCodeRef.current = values.emailCode

            dispatch(
              actions.app.registerRequest({
                emailCode: values.emailCode,
                event: getEventBySlug.data!.uuid!,
                eventSlot: selectedSlotId!,
                optinRegistration: registerFormValues?.opt ?? false,
                visitors: [
                  {
                    first_name: registerFormValues!.firstName,
                    last_name: registerFormValues!.lastName,
                    email: registerFormValues!.email,
                  },
                  ...(registerFormValues?.attendant
                    ? [
                        {
                          first_name: registerFormValues.attendant!.firstName,
                          last_name: registerFormValues.attendant!.lastName,
                          email: registerFormValues.attendant!.email,
                        },
                      ]
                    : []),
                ],
              })
            )
          },
        },
        fields: {
          emailCode: {
            name: 'emailCode',
            numInputs: 5,
          },
        },
        title: t('confirmation.form.title'),
        email: {
          title: t('confirmation.form.email.title'),
          description: t('confirmation.form.email.description', {
            email: registerFormValues!.email,
          }),
        },
        resend: {
          label: t('confirmation.form.resend'),
          disabled: isResendDisabled,
          onClick: () => {
            setResendTimeout()
            dispatch(
              actions.app.sendVerificationCodesRequest({
                firstName: registerFormValues!.firstName,
                event: getEventBySlug.data!.uuid,
                email: registerFormValues!.email,
              })
            )
          },
        },
        submitButton: {
          isPending: register.pending,
          text: t('confirmation.form.submitButton'),
        },
        errors: register.errors,
      },
    }),
    [
      dispatch,
      getEventBySlug.data,
      isResendDisabled,
      layoutProps,
      register.errors,
      registerFormValues,
      setResendTimeout,
      selectedSlotId,
      register.pending,
      t,
    ]
  )
}
